import { DownloadOutlined } from "@ant-design/icons";
import {
  Alert,
  Button,
  Card,
  Col,
  Flex,
  Input,
  Progress,
  Row,
  Select,
  Spin,
  Statistic,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import { ColumnsType } from "antd/es/table";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";
import {
  useLazyGetAllMarketplaceOrdersQuery,
} from "../../../api/marketplace/MarketplaceOrdersApi";
import { useGetRevenueTargetQuery } from "../../../api/admin/sales/RevenueTargetsApi";
import { Redemption } from "../../../api/redemption/Redemption";
import { useGetUsimOrdersByStatusMutation } from "../../../api/usim/orders/UsimOrdersApi";
import { USimStatus } from "../../psim/shared/usimUtils";
import {
  useLazyGetRedemptionsRawQuery,
} from "../../../api/redemption/redemptionApi";
import { createRevenueWorkbook, revenueFilename } from "./exportRevenue";
import {
  buildRevenueOrders,
  formatRevenueOrderDate,
  quarterKey,
  quarterSort,
  RevenueOrder,
  RevenueSource,
  summarizeRevenue,
} from "./revenueData";

const { Title, Text } = Typography;

function formatPhp(cents: number): string {
  return `₱${(cents / 100).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function quarterApiKey(quarter: string): string {
  const [label, year] = quarter.split(" ");
  return `${year}-${label}`;
}

export default function RevenueDashboardPage() {
  const [getRedemptions] = useLazyGetRedemptionsRawQuery();
  const [getMarketplaceOrders] = useLazyGetAllMarketplaceOrdersQuery();
  const [getPhysicalOrders] = useGetUsimOrdersByStatusMutation();
  const [orders, setOrders] = useState<RevenueOrder[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [selectedQuarter, setSelectedQuarter] = useState(
    quarterKey(new Date()),
  );
  const [search, setSearch] = useState("");
  const [source, setSource] = useState<RevenueSource | "all">("all");
  const {
    data: targetData,
    isError: hasTargetError,
  } = useGetRevenueTargetQuery(quarterApiKey(selectedQuarter));

  useEffect(() => {
    let active = true;

    async function loadAllRedemptions(): Promise<Redemption[]> {
      const rows: Redemption[] = [];
      let page = await getRedemptions({}, false).unwrap();
      rows.push(...page.Items);
      while (page.LastEvaluatedKey) {
        page = await getRedemptions({
          lastEvaluatedKey: page.LastEvaluatedKey,
        }, false).unwrap();
        rows.push(...page.Items);
      }
      return rows;
    }

    async function loadDashboard() {
      setIsLoading(true);
      setHasError(false);
      try {
        const [redemptions, marketplace, physical] = await Promise.all([
          loadAllRedemptions(),
          getMarketplaceOrders(undefined, false).unwrap(),
          getPhysicalOrders({ status_list: Object.values(USimStatus) }).unwrap(),
        ]);
        if (active) {
          setOrders(buildRevenueOrders(redemptions, marketplace.Items, physical.items));
        }
      } catch (error) {
        console.error("Revenue dashboard load error:", error);
        if (active) {
          setOrders([]);
          setHasError(true);
        }
      } finally {
        if (active) setIsLoading(false);
      }
    }

    loadDashboard();
    return () => {
      active = false;
    };
  }, [getMarketplaceOrders, getRedemptions, getPhysicalOrders]);

  const quarterOptions = useMemo(() => Array.from(new Set([
    quarterKey(new Date()),
    ...orders.map(order => order.quarter),
  ])).sort(quarterSort).map(quarter => ({
    label: quarter,
    value: quarter,
  })), [orders]);

  const quarterOrders = useMemo(() => (
    orders.filter(order => order.quarter === selectedQuarter)
  ), [orders, selectedQuarter]);
  const summary = useMemo(
    () => summarizeRevenue(quarterOrders),
    [quarterOrders],
  );
  const targetPercentage = targetData?.target
    ? Math.round((summary.totalCents / targetData.target.targetAmountCents) * 100)
    : 0;
  const visibleOrders = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    return quarterOrders.filter(order => (
      (source === "all" || order.source === source)
      && order.orderId.toLowerCase().includes(normalizedSearch)
    ));
  }, [quarterOrders, search, source]);

  const columns: ColumnsType<RevenueOrder> = [
    { title: "Order ID", dataIndex: "orderId" },
    {
      title: "Order Date",
      dataIndex: "orderDate",
      render: formatRevenueOrderDate,
    },
    { title: "Sales Channel", dataIndex: "source" },
    {
      title: "Net Revenue",
      dataIndex: "amountCents",
      align: "right",
      render: formatPhp,
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (status: RevenueOrder["status"]) => (
        <Tag color={status === "Paid" ? "green" : (status === "Refunded" ? "red" : "orange")}>
          {status}
        </Tag>
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (_, order) => (
        <Link to={order.kind === "physical"
          ? "/psim/orders/manage"
          : `/redemption?reference=${encodeURIComponent(order.orderId)}`}>
          View
        </Link>
      ),
    },
  ];

  function handleExport() {
    if (visibleOrders.length === 0) {
      message.warning("No sales orders to export");
      return;
    }
    XLSX.writeFile(
      createRevenueWorkbook(visibleOrders),
      revenueFilename(selectedQuarter),
    );
    message.success(`Exported ${visibleOrders.length} sales orders`);
  }

  return (
    <Flex gap={16} vertical>
      <Flex justify="space-between" align="start" wrap="wrap" gap={8}>
        <div>
          <Title level={3} style={{ margin: 0 }}>Revenue Dashboard</Title>
          <Text type="secondary">
            Track revenue from direct sales and website orders
          </Text>
        </div>
        <Select
          aria-label="Reporting quarter"
          value={selectedQuarter}
          options={quarterOptions}
          onChange={setSelectedQuarter}
          style={{ width: 140 }}
        />
      </Flex>

      {hasError && (
        <Alert
          type="error"
          showIcon
          message="Unable to load complete sales data"
          description="No totals are shown because one or more data sources failed to load."
        />
      )}

      {!hasError && <Spin spinning={isLoading}>
        <Card>
          <Statistic title="Total Sales (Net)" value={formatPhp(summary.totalCents)} />
          <Text type="secondary">{selectedQuarter}</Text>
          <div style={{ marginTop: 16 }}>
            {targetData?.target ? <>
              <Progress
                percent={Math.min(targetPercentage, 100)}
                format={() => `${targetPercentage}%`}
                strokeColor="#52c41a"
              />
              <Text type="secondary">
                Target: {formatPhp(targetData.target.targetAmountCents)}
              </Text>
            </> : (
              <Text type={hasTargetError ? "danger" : "secondary"}>
                {hasTargetError
                  ? "Unable to load the revenue target."
                  : "No target set for this quarter."}
              </Text>
            )}
          </div>
        </Card>

        <Text type="secondary">
          Figures shown are net of payment fees and deductions.
        </Text>

        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24} md={12}>
            <Card>
              <Statistic
                title="Manual Submission"
                value={formatPhp(summary.manualCents)}
              />
              <Text type="secondary">
                {summary.manualPercentage.toFixed(1)}% of total sales
              </Text>
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card>
              <Statistic
                title="Take App"
                value={formatPhp(summary.takeAppCents)}
              />
              <Text type="secondary">
                {summary.takeAppPercentage.toFixed(1)}% of total sales
              </Text>
            </Card>
          </Col>
        </Row>

        <Card title="Sales Orders" style={{ marginTop: 16 }}>
          <Flex gap={8} wrap="wrap" style={{ marginBottom: 16 }}>
            <Input.Search
              aria-label="Search order ID"
              placeholder="Search order ID"
              value={search}
              onChange={event => setSearch(event.target.value)}
              style={{ flex: 1, minWidth: 220 }}
            />
            <Select
              aria-label="Sales channel"
              value={source}
              onChange={setSource}
              style={{ width: 190 }}
              options={[
                { label: "All channels", value: "all" },
                { label: "Manual Submission", value: "Manual Submission" },
                { label: "Take App", value: "Take App" },
              ]}
            />
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              disabled={visibleOrders.length === 0 || hasError}
              onClick={handleExport}
            >
              Export Orders
            </Button>
          </Flex>
          <Table
            rowKey={order => `${order.kind ?? "redemption"}:${order.orderId}`}
            columns={columns}
            dataSource={hasError ? [] : visibleOrders}
            loading={isLoading}
            pagination={{
              defaultPageSize: 20,
              pageSizeOptions: [10, 20, 50, 100],
            }}
            size="small"
            bordered
          />
        </Card>
      </Spin>}
    </Flex>
  );
}
